import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Image } from '@hivebrite/design-system/Image';
import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { toast } from '@hivebrite/design-system/Toast';

import { TRANSLATION_TYPE } from 'hb-react/frontoffice/apps/Forum/constants';
import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { useFollowDiscussion } from 'hb-react/frontoffice/apps/Forum/hooks/queries/useFollowDiscussion';
import type { Discussion } from 'hb-react/frontoffice/apps/Forum/types';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import PublicationText from 'hb-react/shared/components/Publication/PublicationText';
import { Translation } from 'hb-react/shared/components/Translation';

interface DiscussionContentProps {
  discussion: Discussion;
}

const DiscussionContent = ({ discussion }: DiscussionContentProps) => {
  const { currentUser } = useDefaultAppContexts();
  const { appPageConfig } = useForumContext();
  const {
    content,
    cover_picture_url: coverPictureUrl,
    title,
    id,
    is_following: isFollowing,
    user,
  } = discussion;
  const onSuccess = (follow) => {
    toast.success(
      follow
        ? s__('Forum|Successfully followed')
        : s__('Forum|Successfully unfollowed'),
    );
  };
  const { mutate, isPending } = useFollowDiscussion({
    discussionId: id,
    follow: !isFollowing,
    onSuccess,
  });
  const breakpoint = useBreakpoint();

  const enableFollow =
    !!appPageConfig?.config?.enable_users_to_follow_discussions &&
    currentUser?.id !== user.id;

  return (
    // @ts-expect-error incorrect type of children
    <Translation model={{ ...discussion, type: TRANSLATION_TYPE }} htmlContent>
      <Box flexDirection="column" width="100%">
        <Box
          justifyContent="space-between"
          alignItems="flex-start"
          mt={2}
          mb={{ _: 4, m: 8 }}
          flexDirection={breakpoint === 's' ? 'column' : 'row'}
        >
          <TitleStyled as="h1">{title}</TitleStyled>
          {enableFollow && (
            <Button
              onClick={() => mutate()}
              ml={{ _: 0, m: 6 }}
              mt={{ _: 4, m: 0 }}
              isLoading={isPending}
              variant={isFollowing ? 'secondary' : 'primary'}
            >
              {isFollowing ? s__('Forum|Unfollow') : s__('Forum|Follow')}
            </Button>
          )}
        </Box>
        {coverPictureUrl && (
          <Box mb={8}>
            <ImgStyled
              src={coverPictureUrl}
              alt=""
              data-testid="discussion-cover-image"
            />
          </Box>
        )}
        <PublicationText text={content} />
      </Box>
    </Translation>
  );
};

export default DiscussionContent;

const ImgStyled = styled(Image).attrs({
  display: 'block',
  width: '100%',
})`
  aspect-ratio: 1.85;
  object-fit: cover;
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;

const TitleStyled = styled(Box).attrs({
  font: 'h1-semibold',
  flex: 1,
})`
  overflow-wrap: anywhere;
`;
